import { Glass } from "@coach/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { AppShell } from "../components/app-shell.js";
import { newBoardSnapshot } from "../features/tactics/editor.jsx";
import type { FieldKind } from "../features/tactics/scene.js";
import { useSync } from "../providers/sync-provider";
import {
  type BoardRow,
  type BoardSummary,
  createBoard,
  listBoards,
  summarize,
} from "../repos/boards";

/**
 * Tactics — list route (`/tactics`).
 *
 * Shows saved boards as a grid and creates new ones. Opening a card or
 * creating a new board navigates to `/tactics/:boardId` so deep links work
 * and the heavy Konva editor only mounts on its own route.
 */
export function TacticsPage() {
  const { db, orgId, userId, engine, status } = useSync();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ready = Boolean(db && orgId && userId);

  const boards = useQuery<BoardRow[]>({
    queryKey: ["boards", orgId, userId],
    enabled: ready,
    queryFn: () => listBoards(db!, orgId!, userId!),
    refetchInterval: 5000,
  });

  const [search, setSearch] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      const row = await createBoard(db!, {
        orgId: orgId!,
        ownerKind: "coach",
        ownerId: userId!,
        name: defaultName(boards.data ?? []),
        snapshot: newBoardSnapshot(),
      });
      void engine?.syncNow();
      return row;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["boards"], exact: false });
      navigate(`/tactics/${row.id}`);
    },
  });

  const list = boards.data ?? [];
  const q = search.trim().toLowerCase();
  const summaries = list.map(summarize);
  const visible = q ? summaries.filter((b) => b.name.toLowerCase().includes(q)) : summaries;

  return (
    <AppShell title="Taktikboard">
      <div className="flex flex-col gap-5">
        {/* Toolbar */}
        <section className="glass-floating flex flex-wrap items-center gap-3 rounded-3xl px-6 py-5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h1 className="home-display text-[20px] font-bold" style={{ color: "#f4f5f3" }}>
              Taktikboard
            </h1>
            <p className="text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              {ready
                ? `${list.length} ${list.length === 1 ? "Spielzug" : "Spielzüge"} gespeichert${status.kind === "idle" ? "" : ` · ${status.kind}`}`
                : "Lade lokale Datenbank…"}
            </p>
          </div>

          <label className="relative flex h-10 min-w-50 flex-1 items-center sm:max-w-70">
            <Search
              className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
              aria-hidden
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Spielzüge durchsuchen…"
              className="h-full w-full rounded-full border border-white/15 bg-white/4 pl-9 pr-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:shadow-glow"
            />
          </label>

          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!ready || create.isPending}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] transition-shadow hover:shadow-glow disabled:opacity-60"
          >
            <Plus className="size-4" />
            {create.isPending ? "Erstelle…" : "Neuer Spielzug"}
          </button>
        </section>

        {/* Grid / empty state */}
        {visible.length === 0 ? (
          <Glass level="panel" padding="lg">
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Sparkles className="size-8 text-pitch-lime" aria-hidden />
              <p className="text-display-sm font-semibold">Noch kein Spielzug gespeichert</p>
              <p className="max-w-sm text-body-sm text-muted-foreground">
                Lege einen neuen Spielzug an, um Aufstellungen zu skizzieren, Bewegungen zu zeichnen
                und Animationen zu planen.
              </p>
              <button
                type="button"
                onClick={() => create.mutate()}
                disabled={!ready || create.isPending}
                className="mt-2 inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-60"
              >
                <Plus className="size-4" />
                Ersten Spielzug erstellen
              </button>
            </div>
          </Glass>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((b) => (
              <BoardCard key={b.id} summary={b} onOpen={() => navigate(`/tactics/${b.id}`)} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* ─── card ─────────────────────────────────────────────────────────── */

function BoardCard({ summary, onOpen }: { summary: BoardSummary; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left"
      aria-label={`Spielzug ${summary.name} öffnen`}
    >
      <Glass
        level="floating"
        padding="md"
        className="flex flex-col gap-3 transition-shadow group-hover:shadow-glow"
      >
        {summary.cover ? (
          <div
            className="relative aspect-16/10 w-full overflow-hidden rounded-2xl"
            style={{ background: "#0e1417" }}
          >
            <img
              src={summary.cover}
              alt=""
              className="absolute inset-0 h-full w-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : (
          <BoardThumb fieldKind={summary.fieldKind} orientation={summary.orientation} />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-body font-semibold leading-tight">{summary.name}</span>
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {FIELD_KIND_LABEL[summary.fieldKind]} · {formatDate(summary.updatedAt)}
            {summary.frameCount > 1 && ` · ${summary.frameCount} Frames`}
          </span>
        </div>
      </Glass>
    </button>
  );
}

function BoardThumb({
  fieldKind,
  orientation,
}: {
  fieldKind: FieldKind;
  orientation: "landscape" | "portrait";
}) {
  // Inline SVG thumbnail — no Konva needed on the listing page. Linien
  // sind proportional zu einem realen 105×68 m Spielfeld; bei Halbfeld
  // zeigen wir nur eine Hälfte (mit echtem Strafraum + 5-m-Raum +
  // Elfmeterpunkt + Strafraumbogen + Mittelkreis-Hälfte).
  const isBlank = fieldKind === "pitch_blank";
  const isHalf = fieldKind === "pitch_half";
  const isZones = fieldKind === "pitch_zones";
  const bg = isBlank ? "#1a1f23" : "#1f6e3a";
  const line = isBlank ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.88)";
  const isLandscape = orientation === "landscape";

  // ViewBox + Margin
  const vbW = isLandscape ? 320 : 200;
  const vbH = isLandscape ? 200 : 320;
  const m = 8;
  const playW = vbW - 2 * m;
  const playH = vbH - 2 * m;

  // Reale Maße (m). Halbfeld = eine Hälfte des Vollfelds, also 52.5 × 68 m.
  const longMeters = isHalf ? 52.5 : 105;
  const shortMeters = 68;

  // EIN einheitlicher Maßstab für beide Achsen → echtes Seitenverhältnis,
  // kein Stauchen/Strecken. Bei Halbfeld lassen wir das Spielfeld bewusst
  // möglichst groß werden (die freibleibenden Letterbox-Bänder kommen
  // automatisch links/rechts oder oben/unten).
  const longAvail = isLandscape ? playW : playH;
  const shortAvail = isLandscape ? playH : playW;
  const scale = Math.min(longAvail / longMeters, shortAvail / shortMeters);
  const longPx = longMeters * scale;
  const shortPx = shortMeters * scale;
  const perM = scale;
  // Spielfeld-Origin (linke obere Ecke) im viewBox, zentriert.
  const px = isLandscape ? m + (longAvail - longPx) / 2 : m + (shortAvail - shortPx) / 2;
  const py = isLandscape ? m + (shortAvail - shortPx) / 2 : m + (longAvail - longPx) / 2;
  const pw = isLandscape ? longPx : shortPx;
  const ph = isLandscape ? shortPx : longPx;

  // Standard-Pitch-Marks (FIFA, gerundet)
  const PB_LONG = 16.5;
  const PB_SHORT = 40.3;
  const GB_LONG = 5.5;
  const GB_SHORT = 18.32;
  const PEN_SPOT = 11;
  const CIRCLE_R = 9.15;
  const SPOT_R = Math.max(1.2, 0.3 * perM);

  const cx = px + pw / 2;
  const cy = py + ph / 2;

  // Helper: Strafraum/Torraum-Rechteck an einem Ende.
  function boxAtEnd(side: "near" | "far", boxLong: number, boxShort: number) {
    const longSize = boxLong * perM;
    const shortSize = boxShort * perM;
    if (isLandscape) {
      const x = side === "near" ? px : px + pw - longSize;
      const y = cy - shortSize / 2;
      return { x, y, w: longSize, h: shortSize };
    }
    const y = side === "near" ? py : py + ph - longSize;
    const x = cx - shortSize / 2;
    return { x, y, w: shortSize, h: longSize };
  }

  function penaltySpot(side: "near" | "far"): { x: number; y: number } {
    if (isLandscape) {
      return {
        x: side === "near" ? px + PEN_SPOT * perM : px + pw - PEN_SPOT * perM,
        y: cy,
      };
    }
    return {
      x: cx,
      y: side === "near" ? py + PEN_SPOT * perM : py + ph - PEN_SPOT * perM,
    };
  }

  // Strafraumbogen ("D") als Kreissegment ausserhalb des Strafraums,
  // mathematisch exakt am Strafraumrand abgeschnitten.
  function arcPath(side: "near" | "far"): string {
    const r = CIRCLE_R * perM;
    const spot = penaltySpot(side);
    if (isLandscape) {
      const edge = side === "near" ? px + PB_LONG * perM : px + pw - PB_LONG * perM;
      const dx = edge - spot.x;
      const dyy = r * r - dx * dx;
      if (dyy <= 0) return "";
      const dy = Math.sqrt(dyy);
      const sweep = side === "near" ? 1 : 0;
      return `M ${edge} ${cy - dy} A ${r} ${r} 0 0 ${sweep} ${edge} ${cy + dy}`;
    }
    const edge = side === "near" ? py + PB_LONG * perM : py + ph - PB_LONG * perM;
    const dy = edge - spot.y;
    const dxx = r * r - dy * dy;
    if (dxx <= 0) return "";
    const dx = Math.sqrt(dxx);
    const sweep = side === "near" ? 0 : 1;
    return `M ${cx - dx} ${edge} A ${r} ${r} 0 0 ${sweep} ${cx + dx} ${edge}`;
  }

  const midCircleR = CIRCLE_R * perM;

  // Halbfeld: "near" = Tor-Ende, "far" = Mittellinie. Vollfeld: beide Enden.
  const ends: Array<"near" | "far"> = isHalf ? ["near"] : ["near", "far"];

  const pb = ends.map((e) => boxAtEnd(e, PB_LONG, PB_SHORT));
  const gb = ends.map((e) => boxAtEnd(e, GB_LONG, GB_SHORT));
  const spots = ends.map((e) => penaltySpot(e));
  const arcs = ends.map((e) => arcPath(e));

  // Mittellinie: Vollfeld in der Mitte, Halbfeld am "far"-Ende.
  const midLine = isLandscape
    ? {
        x1: isHalf ? px + pw : cx,
        y1: py,
        x2: isHalf ? px + pw : cx,
        y2: py + ph,
      }
    : {
        x1: px,
        y1: isHalf ? py + ph : cy,
        x2: px + pw,
        y2: isHalf ? py + ph : cy,
      };

  return (
    <div
      className="relative aspect-16/10 w-full overflow-hidden rounded-2xl"
      style={{ background: "#0e1417" }}
    >
      <div className="absolute inset-0 flex items-center justify-center p-3">
        <svg
          viewBox={`0 0 ${vbW} ${vbH}`}
          className="h-full"
          style={{
            background: bg,
            aspectRatio: `${vbW}/${vbH}`,
            maxHeight: "100%",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {/* Zone-Tints (gezeichnet innerhalb der echten Pitch-Box) */}
          {isZones &&
            !isBlank &&
            (isLandscape ? (
              <>
                <rect x={px} y={py} width={pw / 3} height={ph} fill="rgba(255,121,143,0.10)" />
                <rect
                  x={px + pw / 3}
                  y={py}
                  width={pw / 3}
                  height={ph}
                  fill="rgba(120,170,255,0.10)"
                />
                <rect
                  x={px + (2 * pw) / 3}
                  y={py}
                  width={pw / 3}
                  height={ph}
                  fill="rgba(151,252,0,0.10)"
                />
              </>
            ) : (
              <>
                <rect x={px} y={py} width={pw} height={ph / 3} fill="rgba(151,252,0,0.10)" />
                <rect
                  x={px}
                  y={py + ph / 3}
                  width={pw}
                  height={ph / 3}
                  fill="rgba(120,170,255,0.10)"
                />
                <rect
                  x={px}
                  y={py + (2 * ph) / 3}
                  width={pw}
                  height={ph / 3}
                  fill="rgba(255,121,143,0.10)"
                />
              </>
            ))}

          {/* Aussenrahmen */}
          <rect x={px} y={py} width={pw} height={ph} fill="none" stroke={line} strokeWidth={2} />

          {/* Mittellinie (oder Halbfeld-Linie am offenen Ende) */}
          <line
            x1={midLine.x1}
            y1={midLine.y1}
            x2={midLine.x2}
            y2={midLine.y2}
            stroke={line}
            strokeWidth={2}
          />

          {/* Mittelkreis (Vollfeld) bzw. Halbkreis am offenen Ende (Halbfeld) */}
          {!isHalf && (
            <circle cx={cx} cy={cy} r={midCircleR} fill="none" stroke={line} strokeWidth={2} />
          )}
          {isHalf && isLandscape && (
            <path
              d={`M ${px + pw} ${cy - midCircleR} A ${midCircleR} ${midCircleR} 0 0 0 ${px + pw} ${cy + midCircleR}`}
              fill="none"
              stroke={line}
              strokeWidth={2}
            />
          )}
          {isHalf && !isLandscape && (
            <path
              d={`M ${cx - midCircleR} ${py + ph} A ${midCircleR} ${midCircleR} 0 0 1 ${cx + midCircleR} ${py + ph}`}
              fill="none"
              stroke={line}
              strokeWidth={2}
            />
          )}

          {/* Mittelpunkt */}
          <circle cx={cx} cy={cy} r={SPOT_R} fill={line} />

          {/* Strafraum + 5-m-Raum + Elfmeterpunkt + Strafraumbogen pro Ende */}
          {pb.map((r, i) => (
            <rect
              key={`pb-${i}`}
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              fill="none"
              stroke={line}
              strokeWidth={2}
            />
          ))}
          {gb.map((r, i) => (
            <rect
              key={`gb-${i}`}
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              fill="none"
              stroke={line}
              strokeWidth={1.5}
            />
          ))}
          {spots.map((s, i) => (
            <circle key={`sp-${i}`} cx={s.x} cy={s.y} r={SPOT_R} fill={line} />
          ))}
          {arcs.map((d, i) =>
            d ? <path key={`ar-${i}`} d={d} fill="none" stroke={line} strokeWidth={1.5} /> : null,
          )}
        </svg>
      </div>
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────────── */

const FIELD_KIND_LABEL: Record<FieldKind, string> = {
  pitch_green: "Rasen",
  pitch_blank: "Leer",
  pitch_zones: "Zonen",
  pitch_half: "Halbfeld",
};

function defaultName(existing: BoardRow[]): string {
  const base = "Spielzug";
  const taken = new Set(existing.map((b) => b.name));
  let n = existing.length + 1;
  while (taken.has(`${base} ${n}`)) n++;
  return `${base} ${n}`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
